import React, { PropsWithChildren, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { themeDefault } from '@column/column-ui-kit';

import type { ThemeType } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';

import { breakpoints, containerBreakpoints } from './breakpoints';
import './themeExtension';
import { themeDark } from './themeDark';
import { themeWarm } from './themeWarm';

const defaultThemeWithBreakpoints = {
  ...themeDefault,
  breakpoints,
  containerBreakpoints,
};

const shellToneClassByThemeId: Record<string, string> = {
  default: 'bg-column-app-bg text-column-app-fg',
  dark: 'bg-column-app-night-bg text-column-app-night-fg',
  warm: 'bg-column-app-warm-bg text-column-app-warm-fg',
};

// Keep in sync with .bg-column-app-*-bg in src/index.css. iOS Safari paints the
// dynamic island and bottom toolbar with this color, so it must match the shell
// background to avoid a visible seam at the safe-area boundaries.
const shellBgByThemeId: Record<string, string> = {
  default: '#f6f6f7',
  dark: '#111317',
  warm: '#fcfaf8',
};

type ThemedShellProps = PropsWithChildren<{ tone: ThemeType | undefined }>;

const ThemedShell = ({ children, tone }: ThemedShellProps) => {
  const toneClass = shellToneClassByThemeId[tone ?? 'default'] ?? shellToneClassByThemeId.default;

  return <div className={`h-full ${toneClass}`}>{children}</div>;
};

export const ThemeProvider: React.FC<PropsWithChildren> = (props) => {
  const themeType = useSessionStore((state) => state.settings.themeType);

  useEffect(() => {
    const root = document.documentElement;
    if (themeType === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    const shellBg = shellBgByThemeId[themeType ?? 'default'] ?? shellBgByThemeId.default;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', shellBg);

    // Body extends behind the iOS safe areas (viewport-fit=cover), so its
    // background is what's painted at the dynamic island and behind the
    // floating Safari toolbar. Keep it in sync with the shell tone.
    document.body.style.backgroundColor = shellBg;
  }, [themeType]);

  const themes = new Map([
    ['default', defaultThemeWithBreakpoints],
    ['dark', themeDark],
    ['warm', themeWarm],
    [undefined, defaultThemeWithBreakpoints],
  ]);

  return (
    <StyledThemeProvider theme={themes.get(themeType) ?? defaultThemeWithBreakpoints}>
      <ThemedShell tone={themeType}>{props.children}</ThemedShell>
    </StyledThemeProvider>
  );
};
